import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAllTickets } from '../services/support';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [newTicketsCount, setNewTicketsCount] = useState(0);
  const [lastCheckedTicketId, setLastCheckedTicketId] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      setHasPermission(permission === 'granted');
    } else if (Notification.permission === 'granted') {
      setHasPermission(true);
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body) => {
    if (hasPermission && 'Notification' in window) {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'support-ticket',
        requireInteraction: false
      });
    }
  }, [hasPermission]);

  // Check for new tickets
  const checkForNewTickets = useCallback(async () => {
    try {
      const tickets = await getAllTickets();
      if (Array.isArray(tickets) && tickets.length > 0) {
        const sortedTickets = [...tickets].sort((a, b) => b.id - a.id);
        const latestTicketId = sortedTickets[0].id;

        if (lastCheckedTicketId === null) {
          // First load - just set the latest ID without showing notifications
          setLastCheckedTicketId(latestTicketId);
          const pendingCount = tickets.filter(t => t.status === 'pending').length;
          setNewTicketsCount(pendingCount);
        } else if (latestTicketId > lastCheckedTicketId) {
          // New tickets detected
          const newTickets = sortedTickets.filter(t => t.id > lastCheckedTicketId);
          const pendingCount = tickets.filter(t => t.status === 'pending').length;
          
          setNewTicketsCount(pendingCount);
          setLastCheckedTicketId(latestTicketId);

          // Show notification for new tickets
          if (newTickets.length > 0) {
            const latestTicket = newTickets[0];
            showBrowserNotification(
              'New Support Ticket',
              `${latestTicket.user_name}: ${latestTicket.subject}`
            );
          }
        } else {
          // Update pending count even if no new tickets
          const pendingCount = tickets.filter(t => t.status === 'pending').length;
          setNewTicketsCount(pendingCount);
        }
      }
    } catch (error) {
      console.error('Failed to check for new tickets:', error);
    }
  }, [lastCheckedTicketId, showBrowserNotification]);

  // Clear notification count and update last checked ID
  const clearNotifications = useCallback(async () => {
    setNewTicketsCount(0);
    try {
      const tickets = await getAllTickets();
      if (Array.isArray(tickets) && tickets.length > 0) {
        const sortedTickets = [...tickets].sort((a, b) => b.id - a.id);
        const latestTicketId = sortedTickets[0].id;
        setLastCheckedTicketId(latestTicketId);
      }
    } catch (error) {
      console.error('Failed to update last checked ticket:', error);
    }
  }, []);

  // Poll for new tickets every 30 seconds
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Initial check
    checkForNewTickets();
    requestNotificationPermission();

    // Set up polling interval
    const interval = setInterval(checkForNewTickets, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [checkForNewTickets, requestNotificationPermission]);

  const value = {
    newTicketsCount,
    clearNotifications,
    requestNotificationPermission,
    hasPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
